import React from "react";
import { Formik, Form } from "formik";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import Dialog from "common/basicComponents/Dialog";
import Typography, {
  TypographyVariant,
  TypographyWeight,
} from "common/basicComponents/Typography";
import Button, {
  ButtonVariant,
  ButtonType,
} from "common/basicComponents/Button";
import Box from "common/basicComponents/Box";
import FormInputField, {
  FormInputFieldType,
} from "common/formFields/FormInputField";
import { useViewport, ViewportType } from "utils/ViewportContext";
import {
  addNotification,
  NotificationVariant,
} from "components/notifications/NotificationSlice";
import { updateCardBilling } from "../BillingActions";

const Buttons = styled.div`
  display: flex;
  flex-direction: ${({ viewport }) =>
    viewport > ViewportType.MOBILE ? "row" : "column-reverse"};
  margin-top: 2.5rem;
  width: 100%;
`;

const EditBillingDialog = ({ open, card, onClose }) => {
  const { t } = useTranslation("billing");
  const { type: viewportType } = useViewport();
  const dispatch = useDispatch();

  const details = card?.billing_details || {};
  const address = details.address || {};

  const onSubmit = async (values, form) => {
    form.setSubmitting(true);
    const billingDetails = {
      name: values.name || null,
      email: values.email || null,
      phone: values.phone || null,
      address: {
        line1: values.line1 || null,
        city: values.city || null,
        postal_code: values.postalCode || null,
        country: values.country || null,
      },
    };
    const ok = await updateCardBilling(card.id, billingDetails);
    form.setSubmitting(false);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("common:form.savedSuccessfully"),
        })
      );
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box padding="0 0 2.5rem">
        <Typography
          variant={TypographyVariant.H4}
          weight={TypographyWeight.BOLD}
          text={t("editBillingDetails")}
        />
      </Box>
      <Formik
        initialValues={{
          name: details.name || "",
          email: details.email || "",
          phone: details.phone || "",
          line1: address.line1 || "",
          city: address.city || "",
          postalCode: address.postal_code || "",
          country: address.country || "",
        }}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="name"
                id="name"
                label={t("cardholderName")}
                disabled={isSubmitting}
              />
            </Box>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="email"
                id="email"
                label={t("email")}
                disabled={isSubmitting}
              />
            </Box>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="phone"
                id="phone"
                label={t("phone")}
                disabled={isSubmitting}
              />
            </Box>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="line1"
                id="line1"
                label={t("addressLine")}
                disabled={isSubmitting}
              />
            </Box>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="city"
                id="city"
                label={t("city")}
                disabled={isSubmitting}
              />
            </Box>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="postalCode"
                id="postalCode"
                label={t("postalCode")}
                disabled={isSubmitting}
              />
            </Box>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="country"
                id="country"
                label={t("country")}
                caption={t("countryHint")}
                disabled={isSubmitting}
              />
            </Box>
            <Buttons viewport={viewportType}>
              <Box
                width={viewportType < ViewportType.TABLET ? "100%" : "50%"}
                padding={
                  viewportType < ViewportType.TABLET
                    ? "0.625rem 0"
                    : "0 1rem 0 0"
                }
              >
                <Button
                  variant={ButtonVariant.OUTLINED}
                  text={t("common:button.cancel")}
                  minWidth="13rem"
                  width="100%"
                  onClick={onClose}
                  disabled={isSubmitting}
                />
              </Box>
              <Box
                width={viewportType < ViewportType.TABLET ? "100%" : "50%"}
                padding={
                  viewportType < ViewportType.TABLET
                    ? "0.625rem 0"
                    : "0 0 0 1rem"
                }
              >
                <Button
                  variant={ButtonVariant.GREEN}
                  text={t("common:button.save")}
                  minWidth="13rem"
                  width="100%"
                  type={ButtonType.SUBMIT}
                  isLoading={isSubmitting}
                />
              </Box>
            </Buttons>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

EditBillingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  card: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

EditBillingDialog.defaultProps = {
  card: null,
};

export default EditBillingDialog;
